import React, { useState, useEffect } from 'react';
import { RefreshCw, Eye, Trash2, CheckCircle, Clock, XCircle, Filter, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  completed: 'bg-green-500/20 text-green-400 border-green-500/50',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/50'
};

const statusLabels = {
  pending: 'Pendente',
  in_progress: 'Em Andamento',
  completed: 'Concluído',
  cancelled: 'Cancelado'
};

const AdminPanel = () => {
  const [solicitations, setSolicitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedSolicitation, setSelectedSolicitation] = useState(null);
  const [expanded, setExpanded] = useState(null);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSolicitations();
  }, [filter]);

  const fetchSolicitations = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status_filter: filter } : {};
      const response = await axios.get(`${API}/solicitations/`, { params });
      setSolicitations(response.data);
    } catch (error) {
      console.error('Erro ao buscar solicitações:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
        logout();
        navigate('/login');
      } else {
        toast.error('Erro ao carregar solicitações');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logout realizado com sucesso');
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/solicitations/${id}`, { status: newStatus });
      toast.success('Status atualizado com sucesso');
      fetchSolicitations();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const deleteSolicitation = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta solicitação?')) return;
    
    try {
      await axios.delete(`${API}/solicitations/${id}`);
      toast.success('Solicitação deletada');
      fetchSolicitations();
      setSelectedSolicitation(null);
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar solicitação');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
  
    return date.toLocaleString("pt-BR", {
      timeZone: "America/Campo_Grande",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Painel Administrativo</h1>
            <p className="text-gray-400">Bem-vindo, {user?.username}!</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchSolicitations}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
              Atualizar
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-600/50 text-red-400 hover:bg-red-600/10"
            >
              <LogOut className="mr-2" size={18} />
              Sair
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-slate-900 border border-blue-900/30 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-4">
            <Filter size={20} className="text-blue-400" />
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Todos", value: "all" },
                { label: "Pendentes", value: "pending" },
                { label: "Em Andamento", value: "in_progress" },
                { label: "Concluídos", value: "completed" },
                { label: "Cancelados", value: "cancelled" }
              ].map((item) => (
                <Button
                  key={item.value}
                  onClick={() => setFilter(item.value)}
                  variant={filter === item.value ? "default" : "outline"}
                  className={
                    filter === item.value
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "border-blue-900/30 text-gray-300 hover:bg-slate-800"
                  }
                >
                  {item.label}
                </Button>
              ))}
            </div>
            <span className="text-gray-400 text-sm">
              {solicitations.length} {solicitations.length === 1 ? 'solicitação' : 'solicitações'}
            </span>
          </div>
        </div>

        {/* Solicitations Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-400">Carregando solicitações...</p>
          </div>
        ) : solicitations.length === 0 ? (
          <div className="text-center py-20 bg-slate-900 border border-blue-900/30 rounded-xl">
            <Clock size={48} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Nenhuma solicitação encontrada</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {solicitations.map((solicitation) => (
              <div
                key={solicitation._id}
                className="bg-slate-900 border border-blue-900/30 rounded-xl p-6 hover:border-blue-600/50 transition-all duration-300"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">{solicitation.name}</h3>
                    <p className="text-sm text-gray-400">{formatDate(solicitation.created_at)}</p>
                  </div>
                  <Badge className={`${statusColors[solicitation.status]} border`}>
                    {statusLabels[solicitation.status]}
                  </Badge>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm">
                    <span className="text-gray-500 w-24">Telefone:</span>
                    <a href={`https://wa.me/55${solicitation.phone}`} className="text-blue-400 hover:underline">
                      {solicitation.phone}
                    </a>
                  </div>
                  {solicitation.email && (
                    <div className="flex items-center text-sm">
                      <span className="text-gray-500 w-24">Email:</span>
                      <span className="text-gray-300">{solicitation.email}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm">
                    <span className="text-gray-500 w-24">Dispositivo:</span>
                    <span className="text-gray-300">{solicitation.service_type}</span>
                  </div>
                  {solicitation.device_type && (
                    <div className="flex items-center text-sm">
                      <span className="text-gray-500 w-24">Problema:</span>
                      <span className="text-gray-300">{solicitation.device_type}</span>
                    </div>
                  )}
                </div>

                <div className="bg-slate-800 rounded-lg p-3 mb-4">
                  <p
                    className={`text-sm text-gray-300 whitespace-pre-wrap break-words ${
                      expanded === solicitation._id ? '' : 'line-clamp-3'
                    }`}
                  >
                    {solicitation.problem_description}
                  </p>
                
                  {solicitation.problem_description.length > 100 && (
                    <button
                      onClick={() =>
                        setExpanded(
                          expanded === solicitation._id ? null : solicitation._id
                        )
                      }
                      className="text-blue-400 text-xs mt-2 hover:underline"
                    >
                      {expanded === solicitation._id ? 'Ver menos' : 'Ver mais'}
                    </button>
                  )}
                </div>

                <div className="flex gap-2">
                  <Select
                    value={solicitation.status}
                    onValueChange={(value) => updateStatus(solicitation._id, value)}
                  >
                    <SelectTrigger className="flex-1 bg-slate-800 border-blue-900/30 text-white text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-blue-900/30">
                      <SelectItem value="pending" className="text-white">Pendente</SelectItem>
                      <SelectItem value="in_progress" className="text-white">Em Andamento</SelectItem>
                      <SelectItem value="completed" className="text-white">Concluído</SelectItem>
                      <SelectItem value="cancelled" className="text-white">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteSolicitation(solicitation._id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
